import type { LibrarySearch } from '../main';
import { parseLibrarySearch } from './collection-filter-routing';

export const settingsItems = [
  {
    id: 'account',
    label: '账号与安全',
    group: '账户',
    description: '管理账户、邮箱验证与账户数据。',
  },
  {
    id: 'usage',
    label: '额度与存储',
    group: '账户',
    description: '查看保存数量、存储空间与抓取额度。',
  },
  {
    id: 'sessions',
    label: '已登录设备',
    group: '账户',
    description: '管理浏览器、App 与扩展的访问权限。',
  },
  {
    id: 'preferences',
    label: '偏好设置',
    group: '应用设置',
    description: '调整外观与界面语言。更改会同步到此账号的其他浏览器。',
  },
  {
    id: 'desktop',
    label: '桌面应用',
    group: '应用设置',
    description: '管理全局快捷键、本地缓存与应用更新。',
  },
  {
    id: 'data',
    label: '导入与导出',
    group: '内容与数据',
    description: '迁入浏览器书签，或导出完整的个人内容库。',
  },
  {
    id: 'feedback',
    label: '帮助与反馈',
    group: '支持',
    description: '告诉我们遇到的问题，或分享你的建议。',
  },
  {
    id: 'integrations',
    label: '应用集成',
    group: '内容与数据',
    description: '连接 Notion、Raindrop 并查看内容保存任务。',
  },
  {
    id: 'help',
    label: '使用与数据说明',
    group: '支持',
    description: '了解保存、整理与账户数据的处理方式。',
  },
] as const;
export type SettingsSectionId = (typeof settingsItems)[number]['id'];
export type DesktopSettingsSectionId =
  | Exclude<SettingsSectionId, 'preferences' | 'desktop'>
  | 'general'
  | 'shortcuts'
  | 'about';
export type SettingsNavigationItem = {
  id: SettingsSectionId | DesktopSettingsSectionId;
  label: string;
  group: string;
  description: string;
  keywords?: readonly string[];
};
const desktopSettingsItems: readonly SettingsNavigationItem[] = [
  ...settingsItems.slice(0, 3),
  {
    id: 'general',
    label: '通用',
    group: '应用',
    description: '外观、语言与本机偏好。账号内的设置会同步到其他浏览器。',
    keywords: [
      '偏好设置',
      '外观',
      '主题',
      '浅色',
      '深色',
      '系统',
      '语言',
      '缓存',
      '离线',
      'general',
      'theme',
      'language',
      'cache',
    ],
  },
  {
    id: 'shortcuts',
    label: '快捷键',
    group: '应用',
    description: '查看应用快捷键，设置这台电脑上的全局搜索快捷键。',
    keywords: ['键盘', '全局', '搜索', 'shortcut', 'keyboard', 'global'],
  },
  ...settingsItems.slice(5).filter((item) => item.id !== 'integrations'),
  {
    id: 'about',
    label: '关于 Kiipu',
    group: '支持',
    description: '查看应用版本与更新。',
    keywords: ['版本', '更新', '诊断', 'about', 'version', 'update'],
  },
];
export function getSettingsItems(desktop = false): readonly SettingsNavigationItem[] {
  return desktop ? desktopSettingsItems : settingsItems.filter((item) => item.id !== 'desktop');
}
export function matchingSettingsItems(query: string, desktop = false) {
  if (!desktop)
    return getSettingsItems(false).filter((item) =>
      `${item.label} ${item.description}`.toLowerCase().includes(query.trim().toLowerCase()),
    );
  const terms = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  return getSettingsItems(desktop).filter((item) => {
    const text = [item.label, item.group, item.description, ...(item.keywords ?? [])]
      .join(' ')
      .toLocaleLowerCase();
    return terms.every((term) => text.includes(term));
  });
}
export function resolveSettingsSection(
  pathname: string,
  hash = '',
  desktop = false,
): SettingsNavigationItem['id'] {
  const value = pathname.split('/')[2] || hash.replace(/^#/, '');
  if (desktop && ['preferences', 'appearance', 'desktop'].includes(value)) return 'general';
  if (value === 'appearance') return 'preferences';
  if (value === 'danger') return 'account';
  if (desktop) return desktopSettingsItems.find((item) => item.id === value)?.id ?? 'general';
  return settingsItems.find((item) => item.id === value)?.id ?? 'preferences';
}
const key = (user: string) => `kiipu.library-return.v1:${user}`;
export function rememberLibraryReturn(user: string, search: LibrarySearch) {
  try {
    sessionStorage.setItem(key(user), JSON.stringify(search));
  } catch {
    /* Storage may be disabled. */
  }
}
export function readLibraryReturn(user?: string): LibrarySearch {
  if (!user) return {};
  try {
    const value = JSON.parse(sessionStorage.getItem(key(user)) || '{}');
    if (!value || Array.isArray(value) || typeof value !== 'object') return {};
    const { desktopAction: _action, ...search } = parseLibrarySearch(value);
    return search;
  } catch {
    return {};
  }
}
